import { ArrowUpDown, ListChecks, MessageSquareText, Search, SlidersHorizontal, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { apiClient, type MentionQuery } from "@/shared/api/api-client";
import { ApiError, INVALID_SEARCH_CODE, errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { Pager } from "@/shared/components/Pager";
import { ToolbarSelect } from "@/shared/components/ToolbarSelect";
import { useShowsCena } from "@/shared/components/cena-visibility";
import { usePolarityScale } from "@/shared/components/polarity-choices";
import { toMention, type Mention } from "@/shared/types/mention";
import {
  MENTION_ORDER_DEFAULT,
  MENTION_ORDER_LABELS,
  MENTION_ORDERS,
  TRASH_ORDERS,
  type MentionOrder
} from "@/shared/vocabulary/mention-orders";
import { formatInteger } from "@/shared/utils/format";
import { RULE_REVIEW_MAX_MENTIONS } from "@/shared/vocabulary/rule-reviews";
import { RuleReviewPanel, type RuleReviewCommand } from "./RuleReviewPanel";
import { SelectionBreakdownPanel } from "./SelectionBreakdownPanel";
import { WhyCard } from "./WhyCard";
import { selectionKey, selectionTarget } from "./selection-target";
import { useMentionFilters } from "./use-mention-filters";
import { useMentionTextView } from "./use-mention-text-view";
import { usePremiseTexts } from "./use-premise-texts";
import { readWhyTextOpen, writeWhyTextOpen } from "./why-text-open";

const SEARCH_DEBOUNCE_MS = 350;
const PAGE_SIZES = [25, 50] as const;
// Referência estável, como a rota entrega: um array novo por render seria
// dependência nova a cada render do hook. Sem `?upload=` na URL, é esta.
const NO_UPLOADS: number[] = [];
// Na Lixeira as ordens por data de descarte fazem sentido; aqui não há
// descartada, e toda chave delas seria NULL.
const ORDER_OPTIONS = MENTION_ORDERS.filter(option => !TRASH_ORDERS.includes(option)).map(option => ({
  value: option,
  label: MENTION_ORDER_LABELS[option]
}));

/** O que a página guarda "por recorte": a página e a seleção valem para
 *  UM recorte e voltam ao começo quando ele muda — derivado da chave, em
 *  vez de um efeito que zera depois da consulta (o que custaria uma
 *  consulta a mais com a página 4 aberta). */
type PerRecorte = { key: string; page: number; selected: ReadonlySet<number>; selectAll: boolean };

/**
 * A página "Justificativas" (2026-10-20): o PORQUÊ da classificação de
 * cada menção — de onde veio a polaridade, a abordagem, os temas e as
 * mensagens-chave —, em cartões de leitura.
 *
 * O recorte é o da tela de menções (a loja da casca) e fica ESCONDIDO: a
 * página não tem painel de filtros nem chips editáveis, só uma linha
 * dizendo que há filtro valendo e levando de volta pra ajustá-lo. O que é
 * dela: a busca de texto DENTRO das justificativas (`why_q`) e a ordem.
 *
 * Falha de CARREGAMENTO com lista na tela mantém a lista e avisa em cima;
 * a consulta velha é cancelada e a resposta atrasada nunca vence a nova.
 */
export function WhyPage({
  token,
  companyId,
  openMention,
  mentionChange,
  uploadFilter = NO_UPLOADS,
  reviewLink = null,
  onConsumeReviewLink,
  canReview = true,
  canGenerateSummary = false
}: {
  token: string;
  companyId: string;
  openMention: (mention: Mention) => void;
  mentionChange: number;
  /** Os uploads de `?upload=` na URL: o único filtro que não mora na loja
   *  compartilhada. A rota entrega a lista memoizada pelo texto. */
  uploadFilter?: number[];
  /** A consultoria que o sino mandou abrir (`?consultoria=<id>`): o painel
   *  abre já nela e a rota tira o parâmetro, pra um F5 não reabrir. */
  reviewLink?: number | null;
  onConsumeReviewLink?: () => void;
  /** Quem pode pedir e aplicar consultoria (Especialista ou acima; o
   *  servidor recusa o resto). Sem isso o botão some e o painel só lê, pelo
   *  link do sino (2026-10-24). */
  canReview?: boolean;
  /** Pode pedir o resumo da IA de uma menção (Técnico ou acima). */
  canGenerateSummary?: boolean;
}) {
  const showsCena = useShowsCena();
  const polarityScale = usePolarityScale();
  const filters = useMentionFilters({
    token,
    uploadFilter,
    showsCena,
    polarityScale
  });
  const { buildQuery, waitingForCatalog, activeChips, scope } = filters;
  // O texto das premissas que a IA cita na polaridade: uma busca por empresa.
  const premiseFor = usePremiseTexts(token, companyId);
  // A preferência da pessoa de ver a postagem ou o resumo (2026-10-31); aqui
  // só se LÊ — a escolha mora na barra da tela de Menções.
  const { view: textView } = useMentionTextView(token, () => undefined);

  const [whyInput, setWhyInput] = useState("");
  const [whyQ, setWhyQ] = useState("");
  const [order, setOrder] = useState<MentionOrder>(MENTION_ORDER_DEFAULT);
  // O interruptor "Texto das publicações": lido no primeiro render.
  const [textOpen, setTextOpenState] = useState<boolean>(readWhyTextOpen);
  const setTextOpen = (next: boolean) => {
    setTextOpenState(next);
    writeWhyTextOpen(next);
  };
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [items, setItems] = useState<Mention[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // O painel "Pedir melhorias à IA" (2026-10-21). Nasce na primeira vez
  // que abre e FICA montado depois (escondido ao fechar): a consultoria em
  // andamento continua sendo acompanhada, e reabrir mostra onde ela está.
  const [reviewMounted, setReviewMounted] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewCommand, setReviewCommand] = useState<RuleReviewCommand | null>(null);
  const reviewNonce = useRef(0);
  // Toda menção que a página já carregou, de qualquer página: o chip de uma
  // proposta abre o drawer dela mesmo que a lista tenha mudado de página.
  const knownMentions = useRef(new Map<number, Mention>());
  const lookupMention = useCallback((id: number) => knownMentions.current.get(id), []);

  // A busca nas justificativas espera a pessoa parar de digitar.
  useEffect(() => {
    const typed = whyInput.trim();
    if (typed === whyQ) return;
    const timer = window.setTimeout(() => setWhyQ(typed), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [whyInput, whyQ]);

  // O recorte da página = o da tela de menções + o que é dela. Página,
  // tamanho e ordem do hook são descartados: aqui valem os desta página.
  const recorte = useMemo(() => {
    const { page: _page, size: _size, order: _order, ...rest } = buildQuery();
    return { ...rest, whyQ: whyQ || undefined } as MentionQuery;
  }, [buildQuery, whyQ]);
  const recorteKey = useMemo(
    () => JSON.stringify({ ...recorte, order, size: pageSize }),
    [recorte, order, pageSize]
  );

  const [state, setState] = useState<PerRecorte>({
    key: recorteKey,
    page: 1,
    selected: new Set(),
    selectAll: false
  });
  const current: PerRecorte =
    state.key === recorteKey
      ? state
      : { key: recorteKey, page: 1, selected: new Set(), selectAll: false };
  const { page, selected, selectAll } = current;
  const patch = (change: Partial<Omit<PerRecorte, "key">>) =>
    setState({ ...current, ...change });

  const buildPageQuery = useCallback(
    (): MentionQuery => ({ ...recorte, order, page, size: pageSize }),
    [recorte, order, page, pageSize]
  );
  const queryKey = useMemo(() => JSON.stringify(buildPageQuery()), [buildPageQuery]);

  // Descarte por CARGA + cancelamento: a resposta de uma consulta antiga
  // não escreve na tela, e a rede não baixa 85 kB que ninguém vai ler.
  const loadSeq = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const [searchWarning, setSearchWarning] = useState<string | null>(null);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    try {
      const result = await apiClient.listMentions(token, buildPageQuery(), {
        signal: abort.signal
      });
      if (seq !== loadSeq.current) return;
      const mentions = result.items.map(toMention);
      for (const mention of mentions) knownMentions.current.set(mention.id, mention);
      setItems(mentions);
      setTotal(result.total);
      setError(null);
      setSearchWarning(null);
    } catch (failure) {
      if (seq !== loadSeq.current || abort.signal.aborted) return;
      if (failure instanceof ApiError && failure.code === INVALID_SEARCH_CODE) {
        // A busca da tela de menções está torta: o aviso fica aqui e a
        // lista anterior segue (a correção é lá).
        setSearchWarning(failure.message);
        return;
      }
      setError(errorMessage(failure, "Não foi possível carregar as justificativas."));
    }
  }, [token, buildPageQuery]);

  // A consulta dispara pela CHAVE do recorte, não pela identidade de
  // `load` (que nasce de novo quando o catálogo de marcas chega) — a
  // mesma regra da tela de menções. Segura enquanto o catálogo que o
  // filtro compartilhado precisa não chegou.
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (waitingForCatalog) return;
    void loadRef.current();
  }, [queryKey, waitingForCatalog, token]);

  useEffect(() => () => controller.current?.abort(), []);

  // O drawer mudou uma menção: a lista se refaz. Pula a montagem.
  const seenMentionChange = useRef(mentionChange);
  useEffect(() => {
    if (seenMentionChange.current === mentionChange) return;
    seenMentionChange.current = mentionChange;
    void loadRef.current();
  }, [mentionChange]);

  // O link do sino: abre o painel já na consultoria. O `consume` vai por ref
  // (a rota o recria a cada render) e o efeito depende só do link.
  const consumeRef = useRef(onConsumeReviewLink);
  consumeRef.current = onConsumeReviewLink;
  useEffect(() => {
    if (!reviewLink) return;
    setReviewCommand({ nonce: ++reviewNonce.current, action: "open", reviewId: reviewLink });
    setReviewMounted(true);
    setReviewOpen(true);
    consumeRef.current?.();
  }, [reviewLink]);

  const list = items ?? [];
  const selectionCount = selectAll ? total : selected.size;
  const isSelected = (id: number) => selectAll || selected.has(id);

  const toggle = (id: number) => {
    if (selectAll) {
      // Desmarcar uma no modo "todas" volta pra seleção explícita: a
      // página inteira menos esta.
      patch({
        selectAll: false,
        selected: new Set(list.map(m => m.id).filter(other => other !== id))
      });
      return;
    }
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    patch({ selected: next });
  };

  const clearSelection = () => patch({ selected: new Set(), selectAll: false });
  const selectPage = () => patch({ selected: new Set([...selected, ...list.map(m => m.id)]) });

  const target = () =>
    selectionTarget({ selectAllFiltered: selectAll, selected, scope, buildQuery: buildPageQuery });
  // A contagem do resumo vale pra UM conjunto: o recorte (sem a página) e
  // os ids marcados. Trocar de página com "todas" ligado não a desatualiza.
  const breakdownKey = selectionKey(selectAll, selected, scope, recorteKey);

  const filterCount = activeChips.length;
  // O "ajustar" leva os uploads de volta: o filtro deles mora na URL.
  const adjustSearch = new URLSearchParams(
    uploadFilter.map<[string, string]>(id => ["upload", String(id)])
  ).toString();
  const hasSelection = selectionCount === null || selectionCount > 0;

  // "Pedir melhorias à IA" só vale pra seleção EXPLÍCITA de 1 a 30 menções:
  // "todas do filtro" confunde, e a consultoria é sobre um padrão, não
  // sobre volume (decisão da dona).
  //
  // E de UM dono só (2026-10-25): a consultoria é sobre uma configuração —
  // a da empresa, ou a da sombra de um concorrente —, e o servidor resolve
  // o dono pelas menções (422 quando há dois). A trava aqui é pelos cards
  // que a página tem; menção marcada em outra página fica pro servidor.
  const ownersOfSelection = new Set(
    list
      .filter(mention => selected.has(mention.id))
      .map(mention => (mention.isCompetitor ? `concorrente:${mention.companyName}` : "empresa"))
  );
  const reviewBlock = selectAll
    ? "Para pedir melhorias, marque as menções uma a uma: \"todas do filtro\" não vale aqui."
    : selected.size === 0
      ? `Marque de 1 a ${RULE_REVIEW_MAX_MENTIONS} menções para pedir melhorias à IA.`
      : selected.size > RULE_REVIEW_MAX_MENTIONS
        ? `Há ${formatInteger(selected.size)} marcadas; a consultoria aceita até ${RULE_REVIEW_MAX_MENTIONS}.`
        : ownersOfSelection.size > 1
          ? "Marque menções de um dono só: a empresa ou um concorrente — a consultoria é sobre uma configuração."
          : null;
  const askReview = () => {
    if (reviewBlock) return;
    setReviewCommand({ nonce: ++reviewNonce.current, action: "compose", mentionIds: [...selected] });
    setReviewMounted(true);
    setReviewOpen(true);
  };

  return (
    <div className={`uploads-page why-page${reviewMounted && reviewOpen ? " has-review-panel" : ""}`}>
      <section className="card upload-history-card">
        <div className="card-title">
          <h3>
            <MessageSquareText size={17} />
            Justificativas{total !== null ? ` · ${formatInteger(total)} menções` : ""}
          </h3>
        </div>
        <p className="downloads-intro">
          O porquê de cada classificação: de onde veio a polaridade, a abordagem, os subtemas e as
          mensagens-chave. Aqui vale o filtro da tela de menções; a busca abaixo procura só no
          texto das justificativas.
        </p>

        {filterCount > 0 && (
          <p className="why-filter-note">
            <SlidersHorizontal size={14} aria-hidden="true" />
            {filterCount === 1
              ? "1 filtro da tela de menções está valendo"
              : `${filterCount} filtros da tela de menções estão valendo`}
            {" · "}
            <Link to={`/empresas/${companyId}/mencoes${adjustSearch ? `?${adjustSearch}` : ""}`}>
              ajustar
            </Link>
          </p>
        )}

        <div className="why-controls">
          <div className="search-field">
            <Search size={17} />
            <input
              name="why-search"
              autoComplete="off"
              aria-label="Buscar nas justificativas"
              value={whyInput}
              onChange={event => setWhyInput(event.target.value)}
              placeholder="Buscar nas justificativas…"
            />
          </div>
          <ToolbarSelect<MentionOrder>
            prefix="Ordem"
            icon={ArrowUpDown}
            ariaLabel="Ordem da lista"
            value={order}
            onChange={setOrder}
            options={ORDER_OPTIONS}
          />
          <label className="why-text-switch">
            <input
              type="checkbox"
              checked={textOpen}
              onChange={event => setTextOpen(event.target.checked)}
            />
            <span>Texto das publicações</span>
          </label>
        </div>

        {searchWarning && (
          <p className="form-error" role="alert">
            {searchWarning}
          </p>
        )}
        {error && (
          <ApiState kind="error" onRetry={() => void load()}>
            {error}
          </ApiState>
        )}
        {items === null && !error && <ApiState kind="loading">Carregando…</ApiState>}

        {items !== null && list.length === 0 && !error && (
          <div className="upload-history-empty">
            <MessageSquareText size={26} />
            <strong>
              {whyQ
                ? "Nenhuma justificativa tem esse texto neste recorte."
                : "Nenhuma menção neste recorte."}
            </strong>
          </div>
        )}

        {list.length > 0 && (
          <>
            <div className="why-bulk">
              <ListChecks size={16} aria-hidden="true" />
              <span>
                {hasSelection
                  ? selectionCount === null
                    ? "Todas as menções do filtro selecionadas"
                    : `${formatInteger(selectionCount)} ${
                        selectionCount === 1 ? "menção selecionada" : "menções selecionadas"
                      }${selectAll ? " (todas do filtro)" : ""}`
                  : "Selecione menções para somar subtemas e mensagens-chave."}
              </span>
              {!selectAll && (
                <button type="button" className="text-button" onClick={selectPage}>
                  Selecionar a página
                </button>
              )}
              {!selectAll && total !== null && total > list.length && (
                <button
                  type="button"
                  className="text-button"
                  onClick={() => patch({ selectAll: true })}
                >
                  Selecionar todas as {formatInteger(total)} do filtro
                </button>
              )}
              {hasSelection && (
                <button type="button" className="text-button" onClick={clearSelection}>
                  Limpar seleção
                </button>
              )}
              {canReview && (
                <>
                  <button
                    type="button"
                    className="secondary-button why-review-button"
                    disabled={reviewBlock !== null}
                    title={reviewBlock ?? "Pedir à IA melhorias nas regras, a partir das menções marcadas"}
                    onClick={askReview}
                  >
                    <Sparkles size={15} aria-hidden="true" />
                    Pedir melhorias à IA
                  </button>
                  <span className="why-review-limit">
                    {reviewBlock ?? `Até ${RULE_REVIEW_MAX_MENTIONS} menções por consultoria.`}
                  </span>
                </>
              )}
            </div>

            {hasSelection && (
              <SelectionBreakdownPanel token={token} target={target} selectionKey={breakdownKey} />
            )}

            <div className="why-list" role="list">
              {list.map(mention => (
                <div key={mention.id} role="listitem">
                  <WhyCard
                    mention={mention}
                    token={token}
                    textOpen={textOpen}
                    textView={textView}
                    canGenerateSummary={canGenerateSummary}
                    selected={isSelected(mention.id)}
                    query={whyQ}
                    onToggleSelect={() => toggle(mention.id)}
                    onOpen={() => openMention(mention)}
                    premiseFor={premiseFor}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {total !== null && (
          <Pager
            page={page}
            size={pageSize}
            onPage={next => patch({ page: next })}
            count={{ total }}
            sizes={PAGE_SIZES}
            onSize={setPageSize}
          />
        )}
      </section>

      {reviewMounted && (
        <RuleReviewPanel
          token={token}
          open={reviewOpen}
          command={reviewCommand}
          lookupMention={lookupMention}
          openMention={openMention}
          onClose={() => setReviewOpen(false)}
          canReview={canReview}
        />
      )}
    </div>
  );
}
